import React from "react";
import { FiArrowRight, FiClock, FiHeart, FiPackage } from "react-icons/fi";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import PageMeta from "./PageMeta";
import "../css/about.scss";

const principles = [
  {
    icon: FiHeart,
    number: "01",
    title: "Choose with care",
    copy: "We look for expressive flavour, thoughtful farming and makers who know every stage of their craft.",
  },
  {
    icon: FiClock,
    number: "02",
    title: "Keep the journey short",
    copy: "Direct relationships and a focused buying rhythm help delicate food arrive in genuinely good condition.",
  },
  {
    icon: FiPackage,
    number: "03",
    title: "Share more, waste less",
    copy: "Consolidated orders and right-sized chilled packing mean a lighter journey from producer to table.",
  },
];

function About() {
  return (
    <main className="about-page">
      <PageMeta
        title="Our Story"
        description={`Discover how ${siteConfig.brandName} brings characterful artisan food closer to Hong Kong tables.`}
      />

      <section className="about-hero" aria-labelledby="about-title">
        <div className="about-shell about-hero__grid">
          <div className="about-hero__copy">
            <p className="about-eyebrow">Our story</p>
            <h1 id="about-title">A better way to bring good food home.</h1>
            <p className="about-hero__lead">
              {siteConfig.brandName} began with a simple belief: remarkable food
              should feel generous, approachable and worth sharing.
            </p>
            <div className="about-hero__actions">
              <Link className="about-button about-button--dark" to="/shop">
                Explore the shop <FiArrowRight aria-hidden="true" />
              </Link>
              <Link className="about-text-link" to="/contact">
                Talk to our team
              </Link>
            </div>
          </div>

          <figure className="about-hero__media">
            <img
              src="/main/about-story-generated.png"
              alt="Unbranded artisan food selection on wooden shelves in a Hong Kong shop"
              fetchpriority="high"
            />
            <figcaption>
              <span>Selected at the source</span>
              <span>Delivered chilled in Hong Kong</span>
            </figcaption>
          </figure>
        </div>
      </section>

      <section className="about-story" aria-labelledby="story-title">
        <div className="about-shell about-story__grid">
          <div className="about-story__intro">
            <p className="about-eyebrow">From small beginnings</p>
            <h2 id="story-title">Built around the pleasure of sharing.</h2>
          </div>
          <div className="about-story__body">
            <p>
              It started with friends gathering around a table and the familiar
              question of where to find excellent cheese without the usual
              distance between a careful selection and the customer.
            </p>
            <p>
              Our answer was to buy thoughtfully and directly. By bringing
              individual orders together, we can work with independent producers,
              protect freshness and offer a changing collection at a fairer price.
            </p>
            <p>
              Today, that same idea guides every arrival—from mature farmhouse
              cheese to cold cuts, pantry staples and seasonal discoveries. The
              assortment evolves, but the purpose stays the same: make the next
              shared meal a little more memorable.
            </p>
          </div>
        </div>
      </section>

      <section className="about-principles" aria-labelledby="principles-title">
        <div className="about-shell">
          <header className="about-section-heading">
            <div>
              <p className="about-eyebrow">What matters to us</p>
              <h2 id="principles-title">Good choices at every step.</h2>
            </div>
            <p>
              A concise collection, close producer relationships and careful cold
              handling shape the way we work.
            </p>
          </header>

          <div className="about-principles__grid">
            {principles.map(({ icon: Icon, number, title, copy }) => (
              <article className="about-principle" key={title}>
                <div className="about-principle__top">
                  <Icon aria-hidden="true" />
                  <span>{number}</span>
                </div>
                <h3>{title}</h3>
                <p>{copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="about-cta" aria-labelledby="about-cta-title">
        <div className="about-shell about-cta__inner">
          <div>
            <p className="about-eyebrow">Set the table</p>
            <h2 id="about-cta-title">Find something worth gathering for.</h2>
          </div>
          <div>
            <p>
              Discover small-batch favourites, useful pairing notes and fresh
              arrivals chosen for Hong Kong homes.
            </p>
            <Link className="about-button about-button--yellow" to="/shop">
              Shop all products <FiArrowRight aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

export default About;
